import { lazy, useCallback, useEffect, useMemo, useState } from "react";

import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { Divider, Label } from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import {
  getLocalStorageItem,
  localStorageIsSupported,
  setLocalStorageItem,
} from "simplydo/core/helpers";
import { initUseQuery } from "simplydo/core/query";
import SearchParams from "simplydo/core/searchParams";
import { OpenAPI } from "simplydo/interfaces";

import actions from "actions";
import api from "api";
import { ChallengesReducer } from "reducers/challenges";
import { useAppSelector } from "store";
import { useUserReload } from "utils/useUserReload";
import util from "utils/utils";

import ChallengeIPStatementAgreement from "components/lib/ChallengeIPStatementAgreement";
import ChallengeStatusBar from "components/lib/ChallengeStatusBar";
import PageLoadError from "components/lib/PageLoadError";
import ReportContentChip from "components/lib/ReportContent/ReportContentChip";
import { SideNav } from "components/lib/SideNav";
import { PlaceholderItem, PlaceholderStack } from "components/lib/UI";

import ChallengeAssessments from "./Assessments";
import ChallengeHeader from "./Header";
import ChallengeIdeas from "./Ideas";
import NewIdea from "./NewIdea";
import ChallengeOverview from "./Overview";
import ChallengePlanner from "./Planner/PlannerMenu";

const ChallengeActivity = lazy(() => import("./Activity"));
const ChallengeQuestions = lazy(() => import("./Questions/Questions"));

export const StyledLabel = styled(Label)`
  line-height: 1.1em !important;
`;

// Wrapper func component for when visiting the project ideas page
// Ensures that if you visit challenges/<challenge_id>/board, the view type is set to board
const ChallengeIdeasProjectWrapper = () => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const updateIdeaViewType = useCallback(
    (newIdeaViewType: string) => {
      dispatch(actions.challenges.setIdeaViewType(newIdeaViewType));
    },
    [dispatch],
  );

  useEffect(() => {
    updateIdeaViewType("board");
  }, [updateIdeaViewType]);

  return (
    <Navigate
      to={`/challenges/${params.id}/ideas${location.search}`}
      replace={true}
    />
  );
};

const Challenge = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const [_searchParams, setSearchParams] = useSearchParams();
  const params = useParams();
  const challengeId = params.id?.replace("assessments", "");

  const ideaViewType = useAppSelector(
    ({ challenges }) => challenges.ideaViewType,
  );
  const user = useAppSelector((state) => state.user);
  const urlOrganisation = useAppSelector(
    (state) => state.organisations?.urlOrganisation,
  );

  const dispatch = useDispatch();
  const reloadUser = useUserReload();
  const addEngagement = useCallback(
    (engagement: string) => dispatch(actions.user.addEngagement(engagement)),
    [dispatch],
  );
  const updateFilters = useCallback(
    (data: Partial<ChallengesReducer>) =>
      dispatch(actions.challenges.updateIdeaFilters(data)),
    [dispatch],
  );
  const onFollow = useCallback(
    (context: string, id: string) =>
      dispatch(actions.user.onFollow(context, id)),
    [dispatch],
  );
  const updateIdeaViewType = useCallback(
    (newIdeaViewType: string) => {
      dispatch(actions.challenges.setIdeaViewType(newIdeaViewType));
    },
    [dispatch],
  );

  const [firstView, setFirstView] = useState(true);
  const [hasSignedStatement, setHasSignedStatement] = useState(false);
  const [hasClickedNewIdea, setHasClickedNewIdea] = useState(false);
  const [creatingNewIdea, setCreatingNewIdea] = useState(false);
  const [ipManagementInfo, setIpManagementInfo] = useState<
    Partial<OpenAPI.GET<"/challenges/{id}/ipstatements/statements">["response"]>
  >({
    userHasSigned: false,
    hasSensitiveInfo: false,
    ipStatementEnabled: false,
  });
  const [ipStatement, setIpStatement] = useState<
    OpenAPI.Schemas["IpStatement"] | null
  >(null);
  const [ipStatementAgreementOpen, setIpStatementAgreementOpen] =
    useState(false);
  const userId = user?._id;
  const orgIpManagementEnabled = util.organisationFeaturesEnabled(user, [
    "ipManagement",
  ]);
  const challengePlannerEnabled = util.organisationFeaturesEnabled(user, [
    "challengePlanner",
  ]);

  const challengeQuery = useQuery(
    initUseQuery(api.challenges.get, [challengeId], {
      isEnabled: !!challengeId,
    }),
  );
  const challenge = challengeQuery.data;
  const canManageChallenge = useMemo(
    () => util.canManageChallenge(user, challenge),
    [user, challenge],
  );

  const myIdeasQuery = useQuery(
    initUseQuery(api.challenges.getIdeas, [challengeId, { scope: "me" }], {
      isEnabled: !!userId && !!challengeId,
    }),
  );
  const myIdeas = myIdeasQuery.data?.ideas || [];

  const assignedAssessmentQuery = useQuery(
    initUseQuery(api.challenges.getAssignedAssessments, [challengeId], {
      isEnabled: !!userId,
    }),
  );
  const myAssignedAssessments = useMemo(
    () => assignedAssessmentQuery.data?.ideas || [],
    [assignedAssessmentQuery.data],
  );

  const [isCollapsed, setIsCollapsed] = useState<boolean | undefined>(
    undefined,
  );
  useEffect(() => {
    setIsCollapsed((prev) => {
      if (location.pathname.includes("/ideas")) {
        if (!theme.sizes.isLargeComputer) {
          return true;
        }
      }
      return prev;
    });
  }, [location, theme]);

  useEffect(() => {
    if (firstView) {
      window.scrollTo(0, 0);
      setFirstView(false);
    }
  }, [firstView]);

  useEffect(() => {
    if (userId) {
      addEngagement("viewedChallenge");
    }
  }, [userId, addEngagement]);

  useEffect(() => {
    updateFilters({
      ideaPage: 1,
      ideaOrder: challenge?.defaultIdeaOrder || "default",
      tagFilter: [],
      groupFilter: [],
      ideaFieldChoices: [],
      ideaFilter: "",
    });
  }, [challenge, updateFilters]);

  useEffect(() => {
    const { ipManagement = {} } = challenge || {};
    if (orgIpManagementEnabled && ipManagement?.isEnabled) {
      api.challenges.getLatestIpStatement(
        challengeId,
        (info) => {
          if (info.ipStatementEnabled) {
            setIpManagementInfo(info);
            setHasSignedStatement(info.userHasSigned);
            setIpStatement(info.activeStatement);
          }
        },
        () => {},
      );
    }
  }, [challengeId, challenge, orgIpManagementEnabled]);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- bug with eslint plugin
  const newIdea = useCallback(() => {
    window.scrollTo(0, 0);
    setHasClickedNewIdea(true);
    if (
      ipStatement &&
      ipManagementInfo.ipStatementEnabled &&
      !hasSignedStatement
    ) {
      setIpStatementAgreementOpen(true);
      return;
    }
    setCreatingNewIdea(true);
    util.newIdea(challenge, user, (idea) => {
      navigate(`/ideas/${idea._id}`);
      if (user) {
        reloadUser();
        onFollow("challenge", idea.challenge);
      }
      setHasClickedNewIdea(false);
      setCreatingNewIdea(false);
    });
  }, [
    user,
    challenge,
    navigate,
    onFollow,
    hasSignedStatement,
    ipStatement,
    ipManagementInfo,
    reloadUser,
  ]);

  useEffect(() => {
    if (
      hasClickedNewIdea &&
      !creatingNewIdea &&
      ipStatement &&
      ipManagementInfo.ipStatementEnabled &&
      hasSignedStatement
    ) {
      newIdea();
    }
  }, [
    hasSignedStatement,
    newIdea,
    ipStatement,
    ipManagementInfo,
    hasClickedNewIdea,
    creatingNewIdea,
  ]);

  const redirectToLogin = useCallback(
    (orgCode?: string) => {
      const currentOrg =
        challenge?.ownerOrganisation ||
        ({ code: orgCode } as OpenAPI.Schemas["Organisation"]);
      const searchParams = new SearchParams(window.location.search);
      const redirectPath = `/login?utm-challenge=${challengeId}${searchParams.toSafeString("&")}&then=/challenges/${challengeId}/ideas/new`;
      if (user && currentOrg) {
        window.location.href = util.getBaseUrl(currentOrg, redirectPath);
      } else {
        navigate(redirectPath);
      }
    },
    [challengeId, challenge, navigate, user],
  );

  const onClickProjectBoardLink = useCallback(() => {
    const hasPreviouslySeenProjectBoardInfo =
      localStorageIsSupported() ?
        !!getLocalStorageItem("hasSeenProjectBoardInfo")
      : true;
    if (
      ideaViewType === "board" &&
      location.pathname === `/challenges/${challengeId}/ideas`
    ) {
      if (!hasPreviouslySeenProjectBoardInfo) {
        toast(
          `The ${t("generic.challenge")} project board has been moved into the "${t("common:capitalise", { key: "generic.ideas" })}" page. You are already in the right place.`,
          { duration: 100000, icon: "ℹ️" },
        );
        setLocalStorageItem("hasSeenProjectBoardInfo", "true");
      }
    } else if (
      localStorageIsSupported() &&
      !hasPreviouslySeenProjectBoardInfo
    ) {
      toast(
        `The ${t("generic.challenge")} project board has been moved into the "${t("common:capitalise", { key: "generic.ideas" })}" page. You can still use the sidebar link to access it, or you can change your ${t("generic.idea")} view directly from the "${t("common:capitalise", { key: "generic.ideas" })}" page.`,
        { duration: 100000, icon: "ℹ️" },
      );
      setLocalStorageItem("hasSeenProjectBoardInfo", "true");
    }
    updateIdeaViewType("board");
    navigate(`/challenges/${challengeId}/ideas?queryView=board`);
  }, [challengeId, navigate, updateIdeaViewType, ideaViewType, location, t]);

  const onClickIdeasLink = useCallback(() => {
    if (localStorageIsSupported()) {
      const ideaViewPreference =
        getLocalStorageItem("ideaViewTypePreference") || "card";
      updateIdeaViewType(
        ["card", "list"].includes(ideaViewPreference) ? ideaViewPreference : (
          "card"
        ),
      );
    } else {
      updateIdeaViewType("card");
    }
    navigate(`/challenges/${params.id}/ideas`);
  }, [navigate, params.id, updateIdeaViewType]);

  const ipStatementHandled =
    !util.organisationFeaturesEnabled(user, ["ipManagement"]) ||
    !ipManagementInfo.ipStatementEnabled ||
    (ipManagementInfo.ipStatementEnabled && ipManagementInfo.userHasSigned);

  const unsubmittedAssessments = useMemo(
    () =>
      myAssignedAssessments?.filter(
        (item) =>
          !item?.assessments?.some(
            (assessment) => assessment?.isSubmitted === true,
          ),
      ),
    [myAssignedAssessments],
  );

  const canViewIdeasTab = useMemo(() => {
    return (
      challenge?.ideaVisibility === "users" ||
      canManageChallenge ||
      util.hasPermission(user, "challenge.viewIdeas", challengeId)
    );
  }, [challenge, canManageChallenge, user, challengeId]);

  const canViewProjectBoardTab = useMemo(() => {
    return (
      user &&
      (canManageChallenge ||
        challenge?.projectManagementVisibility === "users" ||
        util.hasPermission(user, "challenge.viewProjectBoard", challengeId))
    );
  }, [user, canManageChallenge, challengeId, challenge]);

  const canViewActivityTab = useMemo(() => {
    return (
      user &&
      (canManageChallenge ||
        util.hasPermission(user, "challenge.viewActivity", challengeId))
    );
  }, [user, canManageChallenge, challengeId]);

  const canViewPlannerTab = useMemo(() => {
    return (
      user &&
      challengePlannerEnabled &&
      (canManageChallenge ||
        util.hasPermission(user, "challenge.viewPlanner", challengeId))
    );
  }, [user, canManageChallenge, challengeId, challengePlannerEnabled]);

  const canViewAssessmentsTab = useMemo(() => {
    return (
      user &&
      (myAssignedAssessments?.length > 0 ||
        ((canManageChallenge ||
          util.hasPermission(user, "challenge.viewAssessments", challengeId)) &&
          challenge?.ownerIdeaTemplate?.assessments?.rounds &&
          challenge.ownerIdeaTemplate.assessments.rounds.length > 0))
    );
  }, [user, canManageChallenge, challengeId, myAssignedAssessments, challenge]);

  useEffect(() => {
    if (ideaViewType !== "board") {
      return;
    }
    if (canViewProjectBoardTab) {
      return;
    }
    // If the user has a preferred idea view type of board, but is not allowed to view the project board on this challenge, we adjust it back to card
    updateIdeaViewType("card");
    setSearchParams((prev) => {
      prev.delete("queryView");
      return prev;
    });
  }, [
    ideaViewType,
    canViewProjectBoardTab,
    setSearchParams,
    updateIdeaViewType,
  ]);

  if (challengeQuery.isError) {
    const usingOrg = user ? user.ownerOrganisation : urlOrganisation;
    if (window.location.pathname === `/challenges/${challengeId}/ideas/new`) {
      return (
        <PageLoadError
          title={t("challenge.errorNewIdea")}
          message={`Please contact an administrator${usingOrg ? ` for ${usingOrg?.name} ` : " "}to request permission if you think this is incorrect.`}
        />
      );
    }

    return (
      <PageLoadError
        title={t(user ? "challenge.errorUser" : "challenge.errorNoUser")}
        message={
          challengeQuery?.error?.message || "Unable to load at this time"
        }
      />
    );
  }

  return (
    <div>
      {challenge && (
        <Helmet>
          <title>
            {challenge ?
              challenge.name
            : t("common:capitalise", { key: "generic.challenge" })}
          </title>
          <meta
            property="og:title"
            content={
              challenge ?
                challenge.name
              : t("common:capitalise", { key: "generic.challenge" })
            }
          />
          <meta property="og:image" content={challenge.bannerImageUrl} />
          <meta
            property="og:url"
            content={`${util.getBaseUrl(user?.ownerOrganisation)}/challenges/${challenge._id}`}
          />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:site" content="@_simplydo"></meta>
          <meta
            name="twitter:url"
            content={`${util.getBaseUrl(user?.ownerOrganisation)}/challenges/${challenge._id}`}
          />
          <meta name="twitter:title" content={challenge.name} />
          <meta name="twitter:image" content={challenge.bannerImageUrl} />
        </Helmet>
      )}
      {util.organisationFeaturesEnabled(user, ["ipManagement"]) ?
        <ChallengeIPStatementAgreement
          challenge={challenge}
          isOpen={ipStatementAgreementOpen}
          setHasSignedStatement={setHasSignedStatement}
          onClose={() => setIpStatementAgreementOpen(false)}
          ipManagementInfo={ipManagementInfo}
          setIpManagementInfo={setIpManagementInfo}
          ipStatement={ipStatement}
          setIpStatement={setIpStatement}
        />
      : null}
      {user && (
        <ChallengeStatusBar
          challengeId={challengeId}
          newIdea={newIdea}
          myIdeas={myIdeas}
          overviewPage={true}
          redirectToLogin={redirectToLogin}
        />
      )}
      <div>
        <ChallengeHeader
          loading={challengeQuery.isLoading}
          challenge={challenge}
          myIdeas={myIdeas}
          newIdea={newIdea}
          isIdeasPage={true}
          redirectToLogin={redirectToLogin}
        />
        <SideNav
          fluid
          collapsible
          isCollapsed={!!isCollapsed}
          onCollapse={() => setIsCollapsed(true)}
          onExpand={() => setIsCollapsed(false)}
          loading={challengeQuery.isLoading}
          top={120}
          navItems={
            challengeQuery.isLoading ?
              [
                {
                  key: "loading",
                  type: "custom",
                  content: (
                    <PlaceholderStack gap={15}>
                      <PlaceholderItem height={40} />
                      <PlaceholderItem height={40} />
                    </PlaceholderStack>
                  ),
                },
              ]
            : [
                {
                  key: "overview",
                  to: ".",
                  content: t("challenge.details.title"),
                  iconLeft: "info circle",
                  active:
                    window.location.pathname === `/challenges/${params.id}`,
                },
                canViewIdeasTab ?
                  {
                    key: "ideas",
                    content: `${t("common:capitalise", { key: "generic.ideas" })}`,
                    iconLeft: "lightbulb",
                    active:
                      window.location.pathname ===
                        `/challenges/${params.id}/ideas` &&
                      ideaViewType !== "board",
                    onClick: onClickIdeasLink,
                    rightIcon:
                      user && challenge?.ideaCount && challenge.ideaCount > 0 ?
                        <StyledLabel size="tiny" circular>
                          {challenge.ideaCount}
                        </StyledLabel>
                      : null,
                  }
                : null,

                canViewActivityTab ?
                  {
                    key: "activity",
                    to: `activity`,
                    content: "Activity",
                    iconLeft: "chart line",
                    active:
                      window.location.pathname ===
                      `/challenges/${params.id}/activity`,
                  }
                : null,

                canViewAssessmentsTab ?
                  {
                    key: "assessments",
                    to: `assessments`,
                    content:
                      !canManageChallenge ? "Your assessments" : "Assessment",
                    iconLeft: "percent",
                    rightIcon:
                      unsubmittedAssessments?.length > 0 ?
                        <StyledLabel size="tiny" color="red" circular>
                          {unsubmittedAssessments?.length}
                        </StyledLabel>
                      : null,
                    active:
                      window.location.pathname.indexOf(
                        `/challenges/${params.id}/assessments`,
                      ) > -1,
                  }
                : null,

                challenge?.questionsEnabled ?
                  {
                    key: "questions",
                    to: `questions`,
                    content: t("common:capitalise", {
                      key: "generic.questions",
                    }),
                    iconLeft: "talk",
                    active:
                      window.location.pathname.indexOf(
                        `/challenges/${params.id}/questions`,
                      ) > -1,
                    rightIcon: (
                      <>
                        {(
                          challenge?.publicQuestions &&
                          challenge.publicQuestions > 0
                        ) ?
                          <StyledLabel size="tiny" circular>
                            {challenge.publicQuestions}
                          </StyledLabel>
                        : null}
                        {(
                          challenge?.nonPublicQuestions &&
                          challenge.nonPublicQuestions > 0
                        ) ?
                          <StyledLabel size="tiny" circular color="red">
                            {challenge.publicQuestions}
                          </StyledLabel>
                        : null}
                      </>
                    ),
                  }
                : null,

                canViewPlannerTab ?
                  {
                    key: "planner",
                    to: `planner`,
                    content: "Planner",
                    iconLeft: "calendar",
                    active:
                      window.location.pathname.indexOf(
                        `/challenges/${params.id}/planner`,
                      ) > -1,
                  }
                : null,

                canViewProjectBoardTab ?
                  {
                    key: "projectManagement",
                    content: "Project Board",
                    iconLeft: "compass",
                    onClick: onClickProjectBoardLink,
                    active:
                      window.location.pathname ===
                        `/challenges/${params.id}/ideas` &&
                      ideaViewType === "board",
                  }
                : null,
              ].filter((item) => Boolean(item))
          }
          main={
            !challengeQuery.isSuccess ?
              <PlaceholderStack gap={20} style={{}}>
                <PlaceholderItem height={40} style={{ minWidth: "100%" }} />
                <PlaceholderItem height={250} style={{ minWidth: "100%" }} />
                {window.location.pathname === `/challenges/${params.id}` && (
                  <>
                    <PlaceholderItem
                      height={40}
                      style={{ minWidth: "100%", marginTop: 20 }}
                    />
                    <PlaceholderItem
                      height={150}
                      style={{ minWidth: "100%" }}
                    />
                  </>
                )}{" "}
              </PlaceholderStack>
            : <Routes>
                <Route
                  path="*"
                  element={
                    <ChallengeOverview
                      challenge={challenge}
                      newIdea={newIdea}
                      ipStatement={ipStatement}
                      ipManagementInfo={ipManagementInfo}
                      ipStatementHandled={ipStatementHandled}
                      setIpStatementAgreementOpen={setIpStatementAgreementOpen}
                      redirectToLogin={redirectToLogin}
                    />
                  }
                />
                {canViewProjectBoardTab ?
                  <Route
                    path="board"
                    element={<ChallengeIdeasProjectWrapper />}
                  />
                : null}
                <Route
                  path="ideas/new"
                  element={<NewIdea redirectToLogin={redirectToLogin} />}
                />
                {(ideaViewType === "board" ?
                  canViewProjectBoardTab
                : canViewIdeasTab) && (
                  <Route
                    path="ideas"
                    element={
                      <ChallengeIdeas
                        challenge={challengeQuery.data}
                        newIdea={newIdea}
                      />
                    }
                  />
                )}
                {canViewPlannerTab && (
                  <Route
                    path="planner/*"
                    element={<ChallengePlanner challenge={challenge} />}
                  />
                )}
                {canViewAssessmentsTab && (
                  <Route
                    path="assessments/*"
                    element={
                      <ChallengeAssessments challenge={challengeQuery.data} />
                    }
                  />
                )}
                {canViewActivityTab && (
                  <Route
                    path="activity"
                    element={
                      <ChallengeActivity challenge={challengeQuery.data} />
                    }
                  />
                )}
                {challenge?.questionsEnabled && (
                  <Route
                    path="questions"
                    element={
                      <ChallengeQuestions challenge={challengeQuery.data} />
                    }
                  />
                )}
              </Routes>
          }
          afterContent={
            <>
              <Divider />
              <ReportContentChip
                forType="challenge"
                forId={challengeId}
                isCollapsed={!!isCollapsed}
              />
            </>
          }
        />
      </div>
    </div>
  );
};

export default Challenge;
